import { AccountSetting, hasAccountSetting } from '@/services/auth/account-settings';
import { AccountFeatureFlag, accountHasFeatureFlag } from '@/services/auth/feature-flags';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { isBuilderPlan as isBuilderPlanAccount } from '@/services/auth/plan-slugs';
import { PlanFeature, type AugmentedAccount, type AugmentedUser } from '@/services/auth/types';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import type { usePermissions } from '@customerio/hydra-permissions/react';
import type { IconName } from '@customerio/pluma-icons';
import type { NavItemProps as PlumaNavItemProps } from '@customerio/pluma-components/react';
import type { MergedWorkspace, WorkspaceObjectType } from '../../../../features/workspaces';
import { objectTypeIcon, sortObjectTypesByName } from '@/services/db/object-type/entity';
import { AppBundle, AppBundleRoutes } from '../../../../utils/app-bundles';

/** The Home item's id predates the "Home" label; tests and decorations key off this, not the label. */
export const HOME_NAV_ITEM_ID = 'dashboard';

export interface NavItemGroup {
  title?: string;
  items: NavItem[];
}

type PlumaNavItemVisibilityProps = Pick<PlumaNavItemProps, 'isVisibleWhenDeselected' | 'isVisibleWhenSelected'>;

export interface NavItem extends PlumaNavItemVisibilityProps {
  activateOnPipelinesRoutes?: string[];
  activeExceptUrls?: string[];
  activeWhenUrls?: string[];
  ariaLabel?: string;
  children?: NavItem[];
  href?: string;
  iconName?: IconName;
  id: string;
  isCustomizable?: boolean;
  isDropdownMenu?: boolean;
  isGreyedOut?: boolean;
  isHiddenByDefault?: boolean;
  isDisabled?: boolean;
  isMoreMenuOnly?: boolean;
  isVisuallyDisabled?: boolean;
  disabledTooltip?: string;
  label: string;
  showNewLabel?: boolean;
  showPremiumLabel?: boolean;
  defaultIsOpen?: boolean;
  transitionOptions?: TargetTransitionOptions;
}

/**
 * The menu as `generateRoutes` writes it, before the entries that do not apply
 * are dropped: an item, or a child, that the account does not get is `null`.
 */
type NavItemDraft = Omit<NavItem, 'children'> & { children?: readonly (NavItem | null)[] };
type NavItemGroupDraft = { title?: string; items: readonly (NavItemDraft | null)[] };

/** An entry the menu shows: written, and not greyed out. */
function shownNavItems<Item extends NavItemDraft>(items: readonly (Item | null)[]): Item[] {
  return items.flatMap((item) => (item == null || item.isGreyedOut === true ? [] : [item]));
}

/** A group with only its shown items, each with only its shown children. */
function shownNavItemGroup(group: NavItemGroupDraft): NavItemGroup {
  return {
    title: group.title,
    items: shownNavItems(group.items).map(({ children, ...item }) =>
      children == null ? item : { ...item, children: shownNavItems(children) },
    ),
  };
}

const defined = <T>(item: T | null): item is T => item !== null;

function generateConfigureDataGroup(params: {
  isBuilderPlan: boolean;
  canUseGeofences: boolean;
  envPrefix: string;
  envAppPrefix: string;
  collectionsShowPremiumLabel: boolean;
  hasPermission: ReturnType<typeof usePermissions>['hasPermission'];
  hasAnyPermission: ReturnType<typeof usePermissions>['hasAnyPermission'];
}): NavItemGroup {
  const {
    isBuilderPlan,
    canUseGeofences,
    envPrefix,
    envAppPrefix,
    collectionsShowPremiumLabel,
    hasPermission,
    hasAnyPermission,
  } = params;

  // Permission-gated like Imports/Exports below — the geosets routes redirect
  // users lacking journeys/geofences/view, so don't show them a dead entry.
  const geofencesItem =
    canUseGeofences && hasPermission('journeys/geofences/view') === true
      ? {
          id: 'geofences',
          label: 'Geofences',
          // Geofences live inside geosets now; the feature entry points at the
          // geosets index (label stays "Geofences" — the customer-facing name).
          href: `${envPrefix}/geosets`,
          // Base (outline) name on purpose: Pluma's NavItem renders the
          // `-filled` variant itself when the item is selected (preferFilled).
          iconName: 'geofences' as const,
        }
      : null;

  if (isBuilderPlan) {
    return {
      title: 'Configure data',
      items: [
        {
          id: 'data-index',
          label: 'Data index',
          href: `${envPrefix}/data-index`,
          activeWhenUrls: [`${envPrefix}/data-index`, `${envAppPrefix}/data-index`],
          iconName: 'data-index' as const,
        },
        hasPermission('journeys/imports/view') === true
          ? {
              id: 'imports',
              label: 'Imports',
              href: `${envAppPrefix}/imports`,
              iconName: 'import' as const,
            }
          : null,
        hasPermission('journeys/exports/view') === true
          ? {
              id: 'exports',
              label: 'Exports',
              href: `${envAppPrefix}/exports`,
              iconName: 'export' as const,
            }
          : null,
        geofencesItem,
      ].filter(defined),
    };
  }

  return {
    title: 'Configure data',
    items: [
      {
        id: 'data-index',
        label: 'Data index',
        href: `${envPrefix}/data-index`,
        activeWhenUrls: [`${envPrefix}/data-index`, `${envAppPrefix}/data-index`],
        iconName: 'data-index' as const,
      },
      hasAnyPermission('journeys/imports/view', 'journeys/exports/view') === true
        ? {
            id: 'integrations',
            label: 'Integrations',
            // The migrated Hydra page drops the `journeys` segment, so point straight at the URL
            // the user lands on rather than paying for a redirect hop through the twin.
            href: `${envPrefix}/integrations`,
            // The nested connector pages stay reachable at their `journeys` URL — some served by
            // the embedded app (`sql-sync/*`, `audiences`, …), some claimed by Hydra purely to
            // forward to the migrated spelling (`:type`, `warehouse`) — so keep matching both
            // prefixes or the item stops highlighting on every one of them.
            activeWhenUrls: [`${envPrefix}/integrations`, `${envAppPrefix}/integrations`],
            activateOnPipelinesRoutes: ['/pipelines'],
            iconName: 'integrations' as const,
          }
        : null,
      geofencesItem,
      hasPermission('journeys/imports/view') === true
        ? {
            id: 'imports',
            label: 'Imports',
            // The `journeys` spelling, which both flag states serve: the embedded app owns it
            // while the migration flag is off, and the twin route forwards it once the flag is
            // on. Keeping it here costs a redirect hop for the migrated cohort rather than for
            // everyone, and the cutover PR flips it.
            href: `${envAppPrefix}/imports`,
            // The migrated list drops the `journeys` segment, so its URL does not start with the
            // href above and the item would go dark on the very page it links to. Match that
            // prefix too, the way Campaigns and Integrations do for the same reason. No trailing
            // slash here, unlike Campaigns: the migrated URL *is* the index, so a slash would
            // miss it, and nothing else in the tree shares the prefix.
            activeWhenUrls: [`${envAppPrefix}/imports`, `${envPrefix}/imports`],
            iconName: 'import' as const,
            isHiddenByDefault: true,
          }
        : null,
      hasPermission('journeys/exports/view') === true
        ? {
            id: 'exports',
            label: 'Exports',
            href: `${envAppPrefix}/exports`,
            iconName: 'export' as const,
            isHiddenByDefault: true,
          }
        : null,
      {
        id: 'collections',
        label: 'Collections',
        // Hydra page without the `journeys` segment. The old `journeys` URL redirects to it, so
        // keep matching both.
        href: `${envPrefix}/collections`,
        activeWhenUrls: [`${envPrefix}/collections`, `${envAppPrefix}/collections`],
        showPremiumLabel: collectionsShowPremiumLabel,
        iconName: 'database' as const,
        isHiddenByDefault: true,
      },
    ].filter(defined),
  };
}

function shouldShowFullJourneysRoutesMenu(auth: { account: AugmentedAccount | null; user: AugmentedUser | null }) {
  const { user, account } = auth;

  if (user == null || account == null) {
    return false;
  }

  // if masqueraded into an account
  if (account?.is_masq ?? false) {
    // if the user doesn't have all permissions properly set to masq return false
    if (user.noMasqAccess || user.masqLevelNotSet) {
      return false;
    }

    // if the user has masq permissions and accessGrantedUntil is set return true
    if (user.isSuperUser || user.canReadWriteMasq || user.canReadOnlyMasq) {
      return true;
    }

    return false;
  }

  return true;
}

export function generateRoutes(
  currentWorkspace: MergedWorkspace,
  auth: { account: AugmentedAccount | null; user: AugmentedUser | null },
  { hasAnyPermission, hasPermission }: Pick<ReturnType<typeof usePermissions>, 'hasAnyPermission' | 'hasPermission'>,
  options: {
    objectTypes?: readonly WorkspaceObjectType[];
  },
): NavItemGroup[] {
  if (currentWorkspace.cio_workspace_id == null || currentWorkspace.cio_workspace_id === 0) {
    return [];
  }

  const { objectTypes = [] } = options;
  // oxlint-disable-next-line local-rules/enforce-generate-path -- prefix used to build many nav URLs
  const envPrefix = `/workspaces/${currentWorkspace.id}`;
  const envAppPrefix = `${envPrefix}${AppBundleRoutes[AppBundle.Journeys]}`;

  // The report and its Deliverability and Email clients tabs are Hydra pages off
  // `/workspaces/:id` directly. They also answer at the pre-rename `/analysis/...` spelling,
  // which the `/reports` href prefix alone never matches, so cover both.
  const analysisHydraPrefixes = [`${envPrefix}/analysis`, `${envPrefix}/reports`];

  // The migrated People list is a Hydra page off `/workspaces/:id` directly, without the
  // `/journeys` segment the nav href carries, so the href prefix alone never matches it. It ships
  // at both spellings, so cover both.
  const peopleHydraPrefixes = [`${envPrefix}/people`, `${envPrefix}/profiles`];

  // The migrated list sends a flag-off account back to `journeys/segments`, and a segment's
  // unmigrated pages stay there, so the item lights on both spellings. The exception keeps a
  // sibling such as `/segments-archive` dark.
  const segmentsHref = `${envPrefix}/segments`;
  const segmentsPrefixes = [`${envAppPrefix}/segments`];
  const segmentsExceptions = [`${envPrefix}/segments-`];

  // Profile activity is a Hydra page without the `journeys` segment. The href points at the
  // default (identified) tab, so match on the base segment instead — keeps the nav item
  // highlighted across all sub-tabs (identified / anonymous) and the base path.
  const profileActivityHref = `${envPrefix}/profile_activity/identified`;
  const profileActivityBaseHref = `${envPrefix}/profile_activity`;

  // Message activity is a Hydra page without the `journeys` segment. Its index redirects to
  // /message_activity/sent, so match on the base segment to keep the item highlighted across the
  // sent/drafts sub-tabs. The draft detail is still an Ember page at the old `journeys` URL, so
  // match that too.
  const messageActivityHref = `${envPrefix}/message_activity`;
  const messageActivityJourneysHref = `${envAppPrefix}/message_activity`;

  const showFullMenu = shouldShowFullJourneysRoutesMenu(auth);
  const isAnonymousInAppEnabled = auth.account?.planFeatures[PlanFeature.AnonymousInApp].enabled ?? false;
  const isSignUpFormsEnabled = accountHasFeatureFlag(auth.account, AccountFeatureFlag.SignUpForms);
  const isClassicEditorsSunset = accountHasFeatureFlag(auth.account, AccountFeatureFlag.ClassicEditorsSunset);
  const isMasquerading = auth.account?.is_masq ?? false;
  // Lift is not advertised to customers yet. The pages stay reachable by URL for any
  // account with the flag, but the nav keeps its flag-off shape unless an internal user
  // is masquerading into such an account. This one gate drives every Lift nav decision
  // below — the group, where Agent sits, and whether Goals hides — so a
  // customer sees no nav difference at all.
  const showLiftNav = isMasquerading && accountHasFeatureFlag(auth.account, AccountFeatureFlag.OutcomeOptimization);

  const isAiEnabled = hasAccountSetting(AccountSetting.AiEnabled);
  const isBuilderPlan = isBuilderPlanAccount(auth.account);
  const canUseGeofences = auth.account != null && hasUnlimitedPlanFeatureAccess(auth.account, PlanFeature.Geofences);
  if (!showFullMenu) {
    return [];
  }

  return (
    [
      {
        items: [
          {
            id: HOME_NAV_ITEM_ID,
            label: 'Home',
            href: `${envPrefix}/home`,
            iconName: 'home',
            // The classic dashboard is Home's in both spellings: the migrated page
            // drops the `journeys` segment the embedded one carries.
            activeWhenUrls: [
              `${envPrefix}/home`,
              `${envPrefix}/dashboard`,
              `${envAppPrefix}/dashboard`,
              `${envAppPrefix}/home`,
            ],
          },
          // Lift: when the Lift nav shows, Agent moves into that group below, so it
          // is null here to avoid showing twice.
          isAiEnabled && !showLiftNav
            ? {
                id: 'agent',
                label: 'Agent',
                href: `${envPrefix}/agent`,
                iconName: 'sparkle',
              }
            : null,
        ],
      },
      // Lift: Outcomes states a goal and measures lift against a holdout. The whole
      // group rides on showLiftNav; when that is false the items below are null and
      // the empty group drops out.
      {
        title: 'Lift',
        items: [
          // The agent drives the loop, so it leads the group. It needs AI enabled as
          // well as showLiftNav; when it moves here it leaves the Home group above.
          isAiEnabled && showLiftNav
            ? {
                id: 'agent',
                label: 'Agent',
                href: `${envPrefix}/agent`,
                iconName: 'sparkle' as const,
              }
            : null,
          showLiftNav
            ? {
                id: 'outcomes',
                label: 'Outcomes',
                href: `${envPrefix}/outcomes`,
                iconName: 'goal' as const,
              }
            : null,
        ],
      },
      isBuilderPlan
        ? {
            title: 'Review',
            items: [
              {
                id: 'logs',
                label: 'Profile activity',
                href: profileActivityHref,
                activeWhenUrls: [profileActivityBaseHref],
                iconName: 'activity',
              },
              {
                id: 'outbox',
                label: 'Message activity',
                href: messageActivityHref,
                activeWhenUrls: [messageActivityHref, messageActivityJourneysHref],
                iconName: 'delivery-log',
              },
            ],
          }
        : {
            title: 'Review',
            items: [
              // Two independent reasons to hide the standalone Goals page, and they stack.
              // `show-goals` off hides goals everywhere (FLO-4772) — the route renders
              // NotFoundPage, so the item would be a dead link. Lift: Goals is superseded by
              // Outcomes, which moves to its own group, so the two never both show. A customer
              // keeps Goals either way — showLiftNav needs masquerade, not just the flag.
              !accountHasFeatureFlag(auth.account, AccountFeatureFlag.ShowGoals) || showLiftNav
                ? null
                : {
                    id: 'goals',
                    label: 'Goals',
                    href: `${envAppPrefix}/goals`,
                    activeWhenUrls: [`${envAppPrefix}/goals`, `${envPrefix}/goals`],
                    iconName: 'goal' as const,
                  },
              {
                id: 'analysis',
                label: 'Reports',
                href: `${envPrefix}/reports`,
                activeWhenUrls: analysisHydraPrefixes,
                iconName: 'analysis',
              },
              accountHasFeatureFlag(auth.account, AccountFeatureFlag.DashboardEditorPoc)
                ? {
                    id: 'boards',
                    label: 'Boards',
                    href: `${envPrefix}/boards`,
                    iconName: 'dashboard',
                  }
                : null,
              {
                id: 'logs',
                label: 'Profile activity',
                href: profileActivityHref,
                activeWhenUrls: [profileActivityBaseHref],
                iconName: 'activity',
              },
              {
                id: 'outbox',
                label: 'Message activity',
                href: messageActivityHref,
                activeWhenUrls: [messageActivityHref, messageActivityJourneysHref],
                iconName: 'delivery-log',
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Send messages',
            items: [
              {
                id: 'broadcasts',
                label: 'Broadcasts',
                // The migrated list's URL, which sends a flag-off account back to the `journeys`
                // one, as the object pages do. A send's and a broadcast's own pages stay under
                // `journeys`, so the item lights on both spellings.
                href: `${envPrefix}/broadcasts`,
                activeWhenUrls: [`${envPrefix}/broadcasts`, `${envAppPrefix}/broadcasts`],
                iconName: 'broadcast',
              },
              {
                id: 'transactional',
                label: 'Transactional',
                href: `${envAppPrefix}/transactional`,
                iconName: 'transactional-api',
              },
              isSignUpFormsEnabled
                ? {
                    id: 'sign-up-forms',
                    label: 'Sign-up forms',
                    href: `${envPrefix}/sign-up-forms`,
                    iconName: 'form',
                  }
                : null,
            ],
          }
        : {
            title: 'Send messages',
            items: [
              {
                id: 'campaigns',
                label: 'Automations',
                href: `${envAppPrefix}/automations`,
                // The migrated workflow builder drops the `journeys` segment, so its URL does not
                // start with the href above and the item would de-highlight there. Match that
                // prefix too, alongside the list's own, the way Integrations does for the same
                // reason. The trailing slash keeps the match on a segment boundary, so a sibling
                // like `/automations-archive` cannot borrow this item's highlight.
                activeWhenUrls: [`${envAppPrefix}/automations`, `${envPrefix}/automations/`],
                iconName: 'campaigns',
              },
              {
                id: 'broadcasts',
                label: 'Broadcasts',
                // The migrated list's URL, which sends a flag-off account back to the `journeys`
                // one, as the object pages do. A send's and a broadcast's own pages stay under
                // `journeys`, so the item lights on both spellings.
                href: `${envPrefix}/broadcasts`,
                activeWhenUrls: [`${envPrefix}/broadcasts`, `${envAppPrefix}/broadcasts`],
                iconName: 'broadcast',
              },
              {
                id: 'transactional',
                label: 'Transactional',
                href: `${envAppPrefix}/transactional`,
                iconName: 'transactional-api',
              },
              isAnonymousInAppEnabled
                ? {
                    id: 'anonymous',
                    label: 'Anonymous',
                    href: `${envAppPrefix}/anonymous`,
                    iconName: 'anonymous',
                  }
                : null,
              isSignUpFormsEnabled
                ? {
                    id: 'sign-up-forms',
                    label: 'Sign-up forms',
                    href: `${envPrefix}/sign-up-forms`,
                    iconName: 'form',
                  }
                : null,
              {
                // Hydra-native page — sits at `/message-library`, not under
                // the Journeys bundle's `/journeys/message-library` (which is
                // now a legacy redirect to this path).
                id: 'message-library',
                label: 'Message library',
                href: `${envPrefix}/message-library`,
                iconName: 'folder',
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Manage audience',
            items: [
              {
                id: 'people',
                label: 'People',
                href: `${envAppPrefix}/people`,
                activeWhenUrls: peopleHydraPrefixes,
                iconName: 'person',
              },
              {
                id: 'segments',
                label: 'Segments',
                href: segmentsHref,
                activeWhenUrls: segmentsPrefixes,
                activeExceptUrls: segmentsExceptions,
                iconName: 'segment',
              },
            ],
          }
        : {
            title: 'Manage audience',
            items: [
              {
                id: 'people',
                label: 'People',
                href: `${envAppPrefix}/people`,
                activeWhenUrls: peopleHydraPrefixes,
                iconName: 'person',
              },
              ...generateObjectTypeRoutes(envPrefix, envAppPrefix, objectTypes ?? []),
              {
                id: 'segments',
                label: 'Segments',
                href: segmentsHref,
                activeWhenUrls: segmentsPrefixes,
                activeExceptUrls: segmentsExceptions,
                iconName: 'segment',
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Create content',
            items: [
              {
                id: 'design-studio',
                label: 'Design Studio',
                iconName: 'design-studio',
                href: `${envPrefix}/design-studio/dashboard`,
              },
              {
                id: 'assets',
                label: 'Assets',
                href: `${envPrefix}/assets`,
                iconName: 'image',
                activeWhenUrls: [`${envPrefix}/assets`, `${envAppPrefix}/assets`, `${envPrefix}/design-studio/assets`],
              },
              {
                id: 'snippets',
                label: 'Snippets',
                // Hydra-native page — sits at `/snippets`, not under the
                // Journeys bundle's `/journeys/snippets` (which is now a
                // redirect target). See customerio/hydra route migration.
                href: `${envPrefix}/snippets`,
                iconName: 'liquid',
              },
            ],
          }
        : {
            title: 'Create content',
            items: [
              {
                id: 'design-studio',
                label: 'Design Studio',
                iconName: 'design-studio',
                href: `${envPrefix}/design-studio/dashboard`,
              },
              {
                id: 'assets',
                label: 'Assets',
                href: `${envPrefix}/assets`,
                iconName: 'image',
                activeWhenUrls: [`${envPrefix}/assets`, `${envAppPrefix}/assets`, `${envPrefix}/design-studio/assets`],
              },
              isClassicEditorsSunset
                ? null
                : {
                    id: 'layouts',
                    label: 'Email layouts',
                    // Hydra page without the `journeys` segment. The old `journeys` URL
                    // redirects to it, so keep matching both.
                    href: `${envPrefix}/layouts`,
                    activeWhenUrls: [`${envPrefix}/layouts`, `${envAppPrefix}/layouts`],
                    iconName: 'layout',
                    isHiddenByDefault: true,
                  },
              {
                id: 'snippets',
                label: 'Snippets',
                href: `${envPrefix}/snippets`,
                iconName: 'liquid',
                isHiddenByDefault: true,
              },
            ],
          },
      generateConfigureDataGroup({
        isBuilderPlan,
        canUseGeofences,
        envPrefix,
        envAppPrefix,
        collectionsShowPremiumLabel: auth.account?.planFeatures[PlanFeature.Collections].showPremiumLabel ?? true,
        hasPermission,
        hasAnyPermission,
      }),
      auth.account?.id === 1 && !isBuilderPlan
        ? {
            items: [
              {
                id: 'internal-tools',
                label: 'Internal tools',
                href: '/internal-tools',
                iconName: 'a-b-test',
                isMoreMenuOnly: true,
              },
            ],
          }
        : { items: [] },
    ] satisfies NavItemGroupDraft[]
  )
    .map(shownNavItemGroup)
    .filter((group) => group.items.length !== 0);
}

export function isNavItemActive(item: NavItem, currentUrl: string) {
  let isActive = false;

  if (item.activeWhenUrls != null) {
    isActive = item.activeWhenUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive && item.href != null) {
    isActive = currentUrl.startsWith(item.href);
  }

  if (isActive && item.activeExceptUrls != null) {
    isActive = !item.activeExceptUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive) {
    const isPipelinesRoute = currentUrl.includes('/pipelines');
    if (isPipelinesRoute && item.activateOnPipelinesRoutes != null && item.activateOnPipelinesRoutes.length > 0) {
      isActive = item.activateOnPipelinesRoutes.some((route) => route != null && currentUrl.includes(route));
    }
  }

  if (!isActive && item.children != null) {
    isActive = item.children.some((child) => isNavItemActive(child, currentUrl));
  }

  return isActive;
}

// The object pages drop the `journeys` segment, so the hrefs point at the migrated URL, which
// sends a flag-off account back to the `journeys` one. That account then sits on the `journeys`
// spelling, so each item matches it too, or it stops highlighting while the rollout is off.
function generateObjectTypeRoutes(
  envPrefix: string,
  envAppPrefix: string,
  workspaceObjectTypes?: readonly WorkspaceObjectType[],
  isGreyedOut = false,
): NavItem[] {
  const objectTypes = sortObjectTypesByName(workspaceObjectTypes ?? []);

  if (objectTypes.length === 0) {
    return [
      {
        id: 'objects',
        label: 'Custom objects',
        href: `${envPrefix}/objects`,
        activeWhenUrls: [`${envAppPrefix}/objects`],
        iconName: 'object',
        isGreyedOut,
      },
    ];
  }

  if (objectTypes.length <= 3) {
    return objectTypes.map((objectType) => {
      return {
        id: `object-types_${objectType.id}`,
        label: objectType.name,
        href: `${envPrefix}/objects/${objectType.slug}`,
        activeWhenUrls: [`${envAppPrefix}/objects/${objectType.slug}`],
        iconName: objectTypeIcon(objectType),
        isGreyedOut,
      };
    });
  }

  return [
    {
      id: 'custom-objects',
      label: 'Custom objects',
      iconName: 'object',
      isGreyedOut,
      children: objectTypes.map((objectType) => ({
        id: `object-types_${objectType.id}`,
        label: objectType.name,
        href: `${envPrefix}/objects/${objectType.slug}`,
        activeWhenUrls: [`${envAppPrefix}/objects/${objectType.slug}`],
      })),
    },
  ];
}
